// Payroll for the HR module of Khyati Portal / RSR International.
// Auto-calculated from attendance + salary structure. Pay types:
// monthly | daily | piece_rate | contractor. No PF/ESI/PT deductions yet —
// only loan/advance recoveries. Live preview computes on the fly; "Run Payroll"
// persists a DRAFT run which can then be finalized and marked paid.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  HRC, money, fmtDate, Card, SectionTitle, SubTabs, Th, Td, Table,
  Pill, Btn, Stat, MonthPicker, Modal, Empty, Loading,
} from './hrkit';

const num = (v) => Number(v || 0);

const PAY_TYPE_LABEL = (t) => ({
  monthly: 'Monthly', daily: 'Daily wage', piece_rate: 'Piece-rate', contractor: 'Contractor',
}[t] || (t ? String(t).replace(/_/g, ' ') : '—'));

const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) => {
  if (!m) return '—';
  const [y, mo] = String(m).split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

// ───────────────────────────── Payslip modal ─────────────────────────────
function PayslipModal({ id, onClose }) {
  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr('');
    api.get(`/hr/payroll/payslips/${id}`)
      .then((r) => { if (alive) setSlip(r.data?.data || null); })
      .catch((e) => { if (alive) setErr(e.response?.data?.message || 'Could not load payslip'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const lines = slip?.lines || [];
  const earnings = lines.filter((l) => l.line_type === 'earning');
  const deductions = lines.filter((l) => l.line_type === 'deduction');
  const totEarn = earnings.reduce((s, l) => s + num(l.amount), 0);
  const totDed = deductions.reduce((s, l) => s + num(l.amount), 0);
  const net = totEarn - totDed;

  return (
    <Modal title="Payslip" width={560} onClose={onClose}>
      {loading ? <Loading /> : err ? (
        <div style={{ color: HRC.red, fontSize: 13 }}>{err}</div>
      ) : !slip ? <Empty /> : (
        <div>
          {/* Letterhead */}
          <div style={{ border: `1px solid ${HRC.border}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ background: HRC.navy, color: '#fff', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 600, letterSpacing: 0.5 }}>RSR INTERNATIONAL · KHYATI PORTAL</div>
                <div style={{ fontSize: 17, fontWeight: 800, marginTop: 2 }}>{slip.full_name || '—'}</div>
                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                  {slip.employee_code || '—'}{slip.designation ? ` · ${slip.designation}` : ''}{slip.department_name ? ` · ${slip.department_name}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, opacity: 0.75, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Pay period</div>
                <div style={{ fontSize: 15, fontWeight: 800 }}>{monthLabel(slip.period_month)}</div>
                <div style={{ marginTop: 6 }}><Pill value={slip.status} /></div>
              </div>
            </div>

            {/* Attendance context */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, borderBottom: `1px solid ${HRC.border}`, background: HRC.violet }}>
              {[
                ['Pay type', PAY_TYPE_LABEL(slip.pay_type)],
                ['Present', slip.days_present != null ? slip.days_present : (slip.present_days ?? '—')],
                ['Paid days', slip.days_paid != null ? slip.days_paid : (slip.paid_days ?? '—')],
                ['LOP', slip.lop_days ?? 0],
                ...(slip.pay_type === 'piece_rate' && slip.units != null ? [['Units', slip.units]] : []),
                ...(slip.ot_hours != null ? [['OT hrs', slip.ot_hours]] : []),
              ].map(([k, v], i) => (
                <div key={i} style={{ flex: '1 0 90px', padding: '8px 14px', borderRight: `1px solid ${HRC.border}` }}>
                  <div style={{ fontSize: 9, color: HRC.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: HRC.text }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Earnings + deductions */}
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 240px', padding: '14px 18px', borderRight: `1px solid ${HRC.border}` }}>
                <div style={{ fontSize: 10, color: HRC.green, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Earnings</div>
                {earnings.length === 0 ? <div style={{ fontSize: 12, color: HRC.muted }}>—</div> : earnings.map((l, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', color: HRC.text }}>
                    <span>{l.label}</span><span style={{ fontWeight: 600 }}>{money(l.amount)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '8px 0 0', marginTop: 6, borderTop: `1px solid ${HRC.border}`, fontWeight: 800 }}>
                  <span>Gross</span><span>{money(totEarn)}</span>
                </div>
              </div>
              <div style={{ flex: '1 1 240px', padding: '14px 18px' }}>
                <div style={{ fontSize: 10, color: HRC.red, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Deductions</div>
                {deductions.length === 0 ? <div style={{ fontSize: 12, color: HRC.muted }}>No recoveries</div> : deductions.map((l, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', color: HRC.text }}>
                    <span>{l.label}</span><span style={{ fontWeight: 600, color: HRC.red }}>−{money(l.amount)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '8px 0 0', marginTop: 6, borderTop: `1px solid ${HRC.border}`, fontWeight: 800 }}>
                  <span>Total</span><span style={{ color: HRC.red }}>−{money(totDed)}</span>
                </div>
              </div>
            </div>

            {/* Net pay */}
            <div style={{ background: HRC.navy, color: '#fff', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.85 }}>Net Pay</span>
              <span style={{ fontSize: 26, fontWeight: 800 }}>{money(net)}</span>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ───────────────────────────── Run detail ─────────────────────────────
function RunDetail({ runId, onClose, onChanged }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [openSlip, setOpenSlip] = useState(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true); setErr('');
    api.get(`/hr/payroll/runs/${runId}`)
      .then((r) => { if (alive) setRun(r.data?.data || null); })
      .catch((e) => { if (alive) setErr(e.response?.data?.message || 'Could not load run'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [runId]);

  useEffect(load, [load]);

  const setStatus = (status) => {
    setBusy(true); setErr('');
    api.patch(`/hr/payroll/runs/${runId}`, { status })
      .then(() => { load(); onChanged?.(); })
      .catch((e) => setErr(e.response?.data?.message || 'Could not update status'))
      .finally(() => setBusy(false));
  };

  const slips = run?.payslips || [];
  const status = run?.status;

  return (
    <Modal title={`Payroll Run — ${monthLabel(run?.period_month)}`} width={900} onClose={onClose}>
      {loading ? <Loading /> : err ? (
        <div style={{ color: HRC.red, fontSize: 13 }}>{err}</div>
      ) : !run ? <Empty /> : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <div><Pill value={status} /></div>
              <div style={{ fontSize: 12, color: HRC.muted }}>Run date {fmtDate(run.run_date)}</div>
              <div style={{ fontSize: 12, color: HRC.muted }}>{num(run.employee_count)} employees</div>
              {run.processed_by_name && <div style={{ fontSize: 12, color: HRC.muted }}>by {run.processed_by_name}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {status === 'draft' && (
                <Btn variant="primary" disabled={busy} onClick={() => setStatus('finalized')}>Finalize</Btn>
              )}
              {status === 'finalized' && (
                <Btn variant="primary" disabled={busy} style={{ background: HRC.green }} onClick={() => setStatus('paid')}>Mark Paid</Btn>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
            <Stat label="Gross" value={money(run.total_gross)} accent={HRC.navy} />
            <Stat label="Deductions" value={money(run.total_deductions)} accent={HRC.red} />
            <Stat label="Net Payable" value={money(run.total_net)} accent={HRC.green} />
            <Stat label="Employees" value={num(run.employee_count)} accent={HRC.amber} />
          </div>

          {slips.length === 0 ? <Empty>No payslips in this run</Empty> : (
            <Table>
              <thead>
                <tr>
                  <Th>Code</Th><Th>Name</Th><Th>Dept</Th><Th>Type</Th>
                  <Th style={{ textAlign: 'right' }}>Present</Th>
                  <Th style={{ textAlign: 'right' }}>Paid</Th>
                  <Th style={{ textAlign: 'right' }}>LOP</Th>
                  <Th style={{ textAlign: 'right' }}>Gross</Th>
                  <Th style={{ textAlign: 'right' }}>Deductions</Th>
                  <Th style={{ textAlign: 'right' }}>Net</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {slips.map((s) => (
                  <tr key={s.id} onClick={() => setOpenSlip(s.id)} style={{ cursor: 'pointer' }}>
                    <Td style={{ fontWeight: 600 }}>{s.employee_code || '—'}</Td>
                    <Td>{s.full_name || '—'}</Td>
                    <Td style={{ color: HRC.muted }}>{s.department_name || '—'}</Td>
                    <Td style={{ color: HRC.muted }}>{PAY_TYPE_LABEL(s.pay_type)}</Td>
                    <Td style={{ textAlign: 'right' }}>{s.days_present ?? '—'}</Td>
                    <Td style={{ textAlign: 'right' }}>{s.days_paid ?? '—'}</Td>
                    <Td style={{ textAlign: 'right', color: num(s.lop_days) ? HRC.red : HRC.muted }}>{s.lop_days ?? 0}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 600 }}>{money(s.gross_earnings)}</Td>
                    <Td style={{ textAlign: 'right', color: num(s.total_deductions) ? HRC.red : HRC.muted }}>{money(s.total_deductions)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 800, color: HRC.navy }}>{money(s.net_pay)}</Td>
                    <Td><Pill value={s.status} /></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <div style={{ fontSize: 11, color: HRC.muted, marginTop: 10 }}>Click a row to view the full payslip.</div>
        </div>
      )}

      {openSlip && <PayslipModal id={openSlip} onClose={() => setOpenSlip(null)} />}
    </Modal>
  );
}

// ───────────────────────────── Runs tab ─────────────────────────────
function RunsTab({ runs, loading, err, onOpen }) {
  const latest = runs[0];
  return (
    <Card>
      <SectionTitle>Payroll Runs</SectionTitle>

      {latest && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 18 }}>
          <Stat label={`Gross · ${monthLabel(latest.period_month)}`} value={money(latest.total_gross)} accent={HRC.navy} />
          <Stat label="Deductions" value={money(latest.total_deductions)} accent={HRC.red} />
          <Stat label="Net Payable" value={money(latest.total_net)} accent={HRC.green} />
          <Stat label="Employees" value={num(latest.employee_count)} sub={<Pill value={latest.status} />} accent={HRC.amber} />
        </div>
      )}

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {loading ? <Loading /> : runs.length === 0 ? (
        <Empty>No payroll runs yet — pick a month and click “Run / Recompute Payroll”.</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Month</Th><Th>Run Date</Th>
              <Th style={{ textAlign: 'right' }}>Employees</Th>
              <Th style={{ textAlign: 'right' }}>Gross</Th>
              <Th style={{ textAlign: 'right' }}>Deductions</Th>
              <Th style={{ textAlign: 'right' }}>Net</Th>
              <Th>Status</Th>
              <Th style={{ textAlign: 'right' }}>Action</Th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id}>
                <Td style={{ fontWeight: 600 }}>{monthLabel(r.period_month)}</Td>
                <Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.run_date)}</Td>
                <Td style={{ textAlign: 'right' }}>{num(r.employee_count)}</Td>
                <Td style={{ textAlign: 'right' }}>{money(r.total_gross)}</Td>
                <Td style={{ textAlign: 'right', color: num(r.total_deductions) ? HRC.red : HRC.muted }}>{money(r.total_deductions)}</Td>
                <Td style={{ textAlign: 'right', fontWeight: 700, color: HRC.navy }}>{money(r.total_net)}</Td>
                <Td><Pill value={r.status} /></Td>
                <Td style={{ textAlign: 'right' }}>
                  <Btn size="sm" variant="soft" onClick={() => onOpen(r.id)}>Open</Btn>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

// ───────────────────────────── Preview tab ─────────────────────────────
function PreviewTab({ month, data, loading, err }) {
  const slips = data?.payslips || [];
  const totals = data?.totals || { gross: 0, ded: 0, net: 0 };

  return (
    <Card>
      <SectionTitle>Live Preview — {monthLabel(month)}</SectionTitle>

      <div style={{
        fontSize: 12, color: HRC.amber, background: HRC.amber + '12', border: `1px solid ${HRC.amber}33`,
        borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontWeight: 600,
      }}>
        Live preview from current attendance — not saved. Click “Run / Recompute Payroll” to persist a draft.
      </div>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {loading ? <Loading /> : slips.length === 0 ? (
        <Empty>No computable payslips for {monthLabel(month)}. Check attendance &amp; salary structures.</Empty>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 18 }}>
            <Stat label="Gross" value={money(totals.gross)} accent={HRC.navy} />
            <Stat label="Deductions" value={money(totals.ded)} accent={HRC.red} />
            <Stat label="Net Payable" value={money(totals.net)} accent={HRC.green} />
            <Stat label="Employees" value={slips.length} accent={HRC.amber} />
          </div>

          <Table>
            <thead>
              <tr>
                <Th>Code</Th><Th>Name</Th><Th>Type</Th>
                <Th style={{ textAlign: 'right' }}>Pres/Paid</Th>
                <Th style={{ textAlign: 'right' }}>OT hrs</Th>
                <Th style={{ textAlign: 'right' }}>Units</Th>
                <Th style={{ textAlign: 'right' }}>Basic</Th>
                <Th style={{ textAlign: 'right' }}>Allow.</Th>
                <Th style={{ textAlign: 'right' }}>OT amt</Th>
                <Th style={{ textAlign: 'right' }}>Piece</Th>
                <Th style={{ textAlign: 'right' }}>Gross</Th>
                <Th style={{ textAlign: 'right' }}>Loan</Th>
                <Th style={{ textAlign: 'right' }}>Advance</Th>
                <Th style={{ textAlign: 'right' }}>Net</Th>
              </tr>
            </thead>
            <tbody>
              {slips.map((s) => (
                <tr key={s.employee_id} title={s.method ? `Method: ${s.method}` : undefined}>
                  <Td style={{ fontWeight: 600 }}>{s.employee_code || '—'}</Td>
                  <Td>
                    <div>{s.full_name || '—'}</div>
                    {s.method && <div style={{ fontSize: 10, color: HRC.muted }}>{s.method}</div>}
                  </Td>
                  <Td style={{ color: HRC.muted, whiteSpace: 'nowrap' }}>{PAY_TYPE_LABEL(s.pay_type)}</Td>
                  <Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {num(s.present_days)}/{num(s.paid_days)}
                    {num(s.lop_days) > 0 && <span style={{ color: HRC.red }}> ({num(s.lop_days)} LOP)</span>}
                  </Td>
                  <Td style={{ textAlign: 'right' }}>{num(s.ot_hours) || '—'}</Td>
                  <Td style={{ textAlign: 'right' }}>{s.pay_type === 'piece_rate' ? num(s.units) : '—'}</Td>
                  <Td style={{ textAlign: 'right' }}>{money(s.basic)}</Td>
                  <Td style={{ textAlign: 'right' }}>{money(s.allowances)}</Td>
                  <Td style={{ textAlign: 'right' }}>{num(s.overtime_amount) ? money(s.overtime_amount) : '—'}</Td>
                  <Td style={{ textAlign: 'right' }}>{num(s.piece_earnings) ? money(s.piece_earnings) : '—'}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 700 }}>{money(s.gross_earnings)}</Td>
                  <Td style={{ textAlign: 'right', color: num(s.loan_deduction) ? HRC.red : HRC.muted }}>{num(s.loan_deduction) ? money(s.loan_deduction) : '—'}</Td>
                  <Td style={{ textAlign: 'right', color: num(s.advance_deduction) ? HRC.red : HRC.muted }}>{num(s.advance_deduction) ? money(s.advance_deduction) : '—'}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 800, color: HRC.navy }}>{money(s.net_pay)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div style={{ fontSize: 11, color: HRC.muted, marginTop: 10 }}>
            Piece-rate workers are computed by output (units × rate); monthly / daily / contractor by days &amp; OT hours. Hover a row for its calculation method.
          </div>
        </>
      )}
    </Card>
  );
}

// ───────────────────────────── Entry ─────────────────────────────
export default function Payroll(props) {
  const { month, setMonth, onChange } = props || {};
  const m = month || thisMonth();

  const [tab, setTab] = useState('runs');

  // Runs
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsErr, setRunsErr] = useState('');
  const [openRun, setOpenRun] = useState(null);

  // Preview
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState('');
  const [previewedMonth, setPreviewedMonth] = useState(null);

  const [running, setRunning] = useState(false);
  const [banner, setBanner] = useState('');

  const loadRuns = useCallback(() => {
    let alive = true;
    setRunsLoading(true); setRunsErr('');
    api.get('/hr/payroll/runs')
      .then((r) => { if (alive) setRuns(r.data?.data || []); })
      .catch((e) => { if (alive) { setRunsErr(e.response?.data?.message || 'Could not load runs'); setRuns([]); } })
      .finally(() => { if (alive) setRunsLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(loadRuns, [loadRuns]);

  const loadPreview = useCallback((forMonth) => {
    setPreviewLoading(true); setPreviewErr('');
    api.get('/hr/payroll/preview', { params: { month: forMonth } })
      .then((r) => { setPreview(r.data?.data || null); setPreviewedMonth(forMonth); })
      .catch((e) => { setPreviewErr(e.response?.data?.message || 'Could not compute preview'); setPreview(null); })
      .finally(() => setPreviewLoading(false));
  }, []);

  const handlePreview = () => {
    setTab('preview');
    loadPreview(m);
  };

  // Recompute preview when switching to the preview tab or month changes while on it.
  useEffect(() => {
    if (tab === 'preview' && previewedMonth !== m) loadPreview(m);
  }, [tab, m, previewedMonth, loadPreview]);

  const runPayroll = () => {
    setRunning(true); setBanner('');
    api.post('/hr/payroll/runs', { month: m })
      .then((r) => {
        const res = r.data?.data || {};
        setBanner(`Draft payroll for ${monthLabel(m)} created — ${num(res.employee_count)} employees, net ${money(res.total_net)}.`);
        loadRuns();
        onChange?.();
        setTab('runs');
        if (res.runId) setOpenRun(res.runId);
      })
      .catch((e) => setBanner(e.response?.data?.message || 'Could not run payroll'))
      .finally(() => setRunning(false));
  };

  const tabs = [
    { key: 'runs', label: 'Runs' },
    { key: 'preview', label: `Preview (${m})` },
  ];

  return (
    <div>
      {/* Toolbar */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Pay period</div>
            <MonthPicker value={m} onChange={(v) => setMonth?.(v)} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn variant="ghost" disabled={previewLoading} onClick={handlePreview}>Preview</Btn>
            <Btn variant="primary" disabled={running} onClick={runPayroll}>
              {running ? 'Running…' : 'Run / Recompute Payroll'}
            </Btn>
          </div>
        </div>
        {banner && (
          <div style={{
            marginTop: 12, fontSize: 12, fontWeight: 600, padding: '8px 12px', borderRadius: 8,
            color: HRC.green, background: HRC.green + '12', border: `1px solid ${HRC.green}33`,
          }}>{banner}</div>
        )}
      </Card>

      <SubTabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'runs'
        ? <RunsTab runs={runs} loading={runsLoading} err={runsErr} onOpen={setOpenRun} />
        : <PreviewTab month={m} data={preview} loading={previewLoading} err={previewErr} />}

      {openRun && (
        <RunDetail
          runId={openRun}
          onClose={() => setOpenRun(null)}
          onChanged={() => { loadRuns(); onChange?.(); }}
        />
      )}
    </div>
  );
}
